import React, { useRef, useState } from 'react';
import {
  PAGE_W, PAGE_H, PAGE_PAD_X,
  formatRefAndDate, computeTrigram,
} from '../lib/dossierThemes';
import { orgsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

const LOGO_ACCEPTED = 'image/png,image/jpeg,image/jpg,image/webp,image/svg+xml';
const LOGO_MAX_MB = 2;
const LOGO_BASE_W = 150;
const LOGO_BASE_H = 70;

// ───────────────────────────────────────────────────────────────────────────
// CoverPage — a 794×1123 React render of the swiss_grid cover (page de garde).
// Mirrors backend/templates/swiss_grid.html so the editor preview matches the
// exported PDF. In editable mode the title, contact email and availability are
// inline inputs; the org logo can be uploaded/resized/removed.
// ───────────────────────────────────────────────────────────────────────────

export const CoverPage = ({
  name = '',
  title = '',
  setTitle,
  theme,
  logoUrl = null,
  editable = true,
  date,
  orgId = null,
  onLogoChange,
  logoScale = 1,
  setLogoScale,
  showFullName = false,
  email = '',
  setEmail,
  availability = '',
  setAvailability,
  trigram: trigramOverride = '',
  setTrigram,
  brand = '',
}) => {
  const refDate = date || new Date();
  const auto = formatRefAndDate(name, refDate);
  const dateStr = auto.dateStr;
  // A per-dossier trigram override wins over the one computed from the name; it
  // also drives the reference code (RÉF. <trigram>-<year>).
  const trigram = (trigramOverride || '').trim().toUpperCase() || auto.trigram;
  const ref = `${trigram}-${refDate.getFullYear()}`;
  const dispo = (availability || '').trim() || dateStr;

  const logoInputRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);

  const scale = Math.min(2, Math.max(0.4, Number(logoScale) || 1));
  const logoW = Math.round(LOGO_BASE_W * scale);
  const logoH = Math.round(LOGO_BASE_H * scale);

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (!file) return;
    if (!orgId) {
      toast({ title: 'Erreur', description: 'Organisation introuvable.', variant: 'destructive' });
      return;
    }
    if (file.size > LOGO_MAX_MB * 1024 * 1024) {
      toast({ title: 'Fichier trop lourd', description: `Le logo doit faire moins de ${LOGO_MAX_MB} Mo.`, variant: 'destructive' });
      return;
    }
    setLogoBusy(true);
    try {
      const { logo_url } = await orgsAPI.uploadLogo(orgId, file);
      onLogoChange?.(logo_url);
      toast({ title: 'Logo mis à jour', description: 'Il apparaîtra aussi dans le PDF généré.' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de téléverser le logo.', variant: 'destructive' });
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!orgId) return;
    setLogoBusy(true);
    try {
      await orgsAPI.deleteLogo(orgId);
      onLogoChange?.(null);
      setLogoMenuOpen(false);
      toast({ title: 'Logo supprimé' });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de supprimer le logo.', variant: 'destructive' });
    } finally {
      setLogoBusy(false);
    }
  };

  const inputReset = {
    background: 'transparent', border: 'none', outline: 'none',
    padding: 0, margin: 0, fontFamily: 'inherit', width: '100%', cursor: 'text',
  };
  const mono = "'SF Mono','Liberation Mono','Courier New',monospace";
  const editHoverCss = `
    .cover-edit-input { border-radius: 4px; transition: background 0.12s, box-shadow 0.12s; }
    .cover-edit-input:hover { background: rgba(37,99,235,0.06) !important; box-shadow: 0 0 0 1px rgba(37,99,235,0.18); }
    .cover-edit-input:focus { background: rgba(37,99,235,0.09) !important; box-shadow: 0 0 0 1.5px rgba(37,99,235,0.45); }
  `;

  // ── Editorial layout (Bloc éditorial): centered serif initials, kicker,
  //    numbered-section identity. Mirrors backend/templates/bloc_editorial.html.
  if (theme.layout === 'editorial') {
    const serif = "'Liberation Serif', Georgia, 'Times New Roman', serif";
    const ED_PAD = 96;
    return (
      <div style={{ position: 'relative', width: PAGE_W, height: PAGE_H, background: theme.coverBg || '#fff', overflow: 'hidden', fontFamily: "'Liberation Sans', Arial, Helvetica, sans-serif" }}>
        {editable && <style>{editHoverCss}</style>}
        {editable && <input ref={logoInputRef} type="file" accept={LOGO_ACCEPTED} style={{ display: 'none' }} onChange={handleLogoFile} />}

        {/* Kicker top-left: short rule + label */}
        <div style={{ position: 'absolute', top: 150, left: ED_PAD, right: ED_PAD }}>
          <span style={{ display: 'inline-block', width: 34, height: 2, background: theme.ink, verticalAlign: 'middle', marginRight: 16 }} />
          <span style={{ display: 'inline-block', verticalAlign: 'middle', fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: theme.faint }}>Dossier de compétences</span>
        </div>

        {/* Logo top-right */}
        <div style={{ position: 'absolute', top: 132, right: ED_PAD, textAlign: 'right' }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" style={{ display: 'block', marginLeft: 'auto', maxWidth: logoW, maxHeight: logoH, objectFit: 'contain' }} />
          ) : editable ? (
            <button type="button" disabled={logoBusy} onClick={() => logoInputRef.current?.click()} title="Cliquer pour ajouter votre logo"
              style={{ width: 130, height: 52, border: `1px dashed ${theme.faint}`, borderRadius: 6, fontSize: 9.5, color: theme.faint, letterSpacing: 1.5, textTransform: 'uppercase', background: 'transparent', cursor: logoBusy ? 'wait' : 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
              {logoBusy ? 'Envoi…' : 'Votre logo'}
            </button>
          ) : null}
        </div>

        {/* Centered title block */}
        <div style={{ position: 'absolute', top: 0, left: ED_PAD, right: ED_PAD, height: PAGE_H, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          {editable ? (
            <input className="cover-edit-input" value={trigramOverride} onChange={(e) => setTrigram?.(e.target.value.toUpperCase().slice(0, 5))} placeholder={auto.trigram} maxLength={5} title="Trigramme (laisser vide pour le générer depuis le nom)"
              style={{ ...inputReset, textAlign: 'center', fontFamily: serif, fontSize: 76, fontWeight: 700, lineHeight: 1, letterSpacing: 6, color: theme.ink, textTransform: 'uppercase' }} />
          ) : (
            <div style={{ fontFamily: serif, fontSize: 76, fontWeight: 700, lineHeight: 1, letterSpacing: 6, color: theme.ink }}>{trigram}</div>
          )}
          <div style={{ width: 64, height: 1, background: theme.hair, margin: '30px 0 26px' }} />
          {editable ? (
            <textarea className="cover-edit-input" value={title} onChange={(e) => setTitle?.(e.target.value)} placeholder="Titre / poste" rows={2}
              style={{ ...inputReset, textAlign: 'center', resize: 'none', overflow: 'hidden', fontSize: 30, fontWeight: 700, lineHeight: 1.2, letterSpacing: -0.3, color: theme.ink }} />
          ) : (
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2, letterSpacing: -0.3, color: theme.ink }}>{title || 'Titre / poste'}</div>
          )}
        </div>

        {/* Footer: Contact (left) / Société (right) */}
        <div style={{ position: 'absolute', left: ED_PAD, right: ED_PAD, bottom: 64, borderTop: `1px solid ${theme.hair}`, paddingTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: theme.faint, marginBottom: 6 }}>Contact</div>
            {editable ? (
              <input className="cover-edit-input" value={email} onChange={(e) => setEmail?.(e.target.value)} placeholder="email@exemple.com"
                style={{ ...inputReset, fontFamily: mono, fontSize: 12, color: theme.text }} />
            ) : (
              <div style={{ fontFamily: mono, fontSize: 12, color: theme.text }}>{email || '—'}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', paddingLeft: 16 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: theme.faint, marginBottom: 6 }}>Société</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.ink }}>{brand || '—'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: PAGE_W,
        height: PAGE_H,
        background: theme.coverBg || '#fff',
        overflow: 'hidden',
        fontFamily: "'Liberation Sans', Arial, Helvetica, sans-serif",
      }}
    >
      {editable && (
        <style>{`
          .cover-edit-input { border-radius: 4px; transition: background 0.12s, box-shadow 0.12s; }
          .cover-edit-input:hover { background: rgba(37,99,235,0.06) !important; box-shadow: 0 0 0 1px rgba(37,99,235,0.18); }
          .cover-edit-input:focus { background: rgba(37,99,235,0.09) !important; box-shadow: 0 0 0 1.5px rgba(37,99,235,0.45); }
        `}</style>
      )}

      {/* ── Header bar: Réf · Date · Confidentiel ── */}
      <div style={{
        position: 'absolute', top: 56, left: PAGE_PAD_X, right: PAGE_PAD_X,
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, color: theme.ink }}>RÉF. {ref}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, color: theme.ink }}>{dateStr.toUpperCase()}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1.5, color: theme.accent }}>CONFIDENTIEL</span>
      </div>
      <div style={{ position: 'absolute', top: 82, left: PAGE_PAD_X, right: PAGE_PAD_X, height: 2, background: theme.ink }} />

      {/* ── Logo zone ── */}
      <div style={{ position: 'absolute', top: 116, left: PAGE_PAD_X }}>
        {editable && (
          <input ref={logoInputRef} type="file" accept={LOGO_ACCEPTED} style={{ display: 'none' }} onChange={handleLogoFile} />
        )}
        {logoUrl ? (
          editable ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                type="button"
                onClick={() => setLogoMenuOpen((o) => !o)}
                title="Modifier le logo"
                style={{ display: 'block', padding: 0, border: '1px solid transparent', borderRadius: 4, background: 'transparent', cursor: 'pointer', outline: logoMenuOpen ? `1px dashed ${theme.faint}` : 'none' }}
              >
                <img src={logoUrl} alt="Logo" style={{ display: 'block', maxWidth: logoW, maxHeight: logoH, objectFit: 'contain', pointerEvents: 'none' }} />
              </button>
              {logoMenuOpen && (
                <>
                  <div onClick={() => setLogoMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 4 }} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 5, background: '#111827', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 7, width: 190, boxShadow: '0 8px 22px rgba(0,0,0,0.3)' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button type="button" disabled={logoBusy} onClick={() => logoInputRef.current?.click()} style={logoBtn}>{logoBusy ? '…' : 'Remplacer'}</button>
                      <button type="button" disabled={logoBusy} onClick={handleLogoRemove} style={{ ...logoBtn, color: '#FCA5A5' }}>Retirer</button>
                    </div>
                    {setLogoScale && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, color: '#cbd5e1' }}>
                        Taille
                        <input type="range" min={0.4} max={2} step={0.05} value={scale} onChange={(e) => setLogoScale(parseFloat(e.target.value))} style={{ flex: 1, accentColor: theme.accent }} />
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <img src={logoUrl} alt="Logo" style={{ display: 'block', maxWidth: logoW, maxHeight: logoH, objectFit: 'contain' }} />
          )
        ) : editable ? (
          <button
            type="button"
            disabled={logoBusy}
            onClick={() => logoInputRef.current?.click()}
            style={{ width: 150, height: 60, border: `1px dashed ${theme.faint}`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, color: theme.faint, letterSpacing: 1.5, textTransform: 'uppercase', background: 'transparent', cursor: logoBusy ? 'wait' : 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
            title="Cliquer pour ajouter votre logo"
          >{logoBusy ? 'Envoi…' : 'Votre logo'}</button>
        ) : (
          <div style={{ width: 150, height: 60, border: `1px dashed ${theme.faint}`, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, color: theme.faint, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 600 }}>Votre logo</div>
        )}
      </div>

      {/* ── Title block (lower) ── */}
      <div style={{ position: 'absolute', left: PAGE_PAD_X, right: PAGE_PAD_X, bottom: 168 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3.5, textTransform: 'uppercase', color: theme.accent, marginBottom: 14 }}>
          Dossier de compétences
        </div>
        {editable ? (
          <input
            className="cover-edit-input"
            value={trigramOverride}
            onChange={(e) => setTrigram?.(e.target.value.toUpperCase().slice(0, 5))}
            placeholder={auto.trigram}
            maxLength={5}
            title="Trigramme (laisser vide pour le générer depuis le nom)"
            style={{ ...inputReset, fontSize: 150, fontWeight: 800, lineHeight: 0.92, letterSpacing: -6, color: theme.ink, marginBottom: 18, textTransform: 'uppercase' }}
          />
        ) : (
          <div style={{ fontSize: 150, fontWeight: 800, lineHeight: 0.92, letterSpacing: -6, color: theme.ink, marginBottom: 18 }}>
            {trigram}
          </div>
        )}
        {showFullName && name && (
          <div style={{ fontSize: 14, fontWeight: 400, letterSpacing: 3, textTransform: 'uppercase', color: theme.muted, marginBottom: 14 }}>
            {name}
          </div>
        )}
        {editable ? (
          <textarea
            className="cover-edit-input"
            value={title}
            onChange={(e) => setTitle?.(e.target.value)}
            placeholder="Titre / poste"
            rows={2}
            style={{ ...inputReset, resize: 'none', overflow: 'hidden', fontSize: 38, fontWeight: 700, lineHeight: 1.08, letterSpacing: -0.8, color: theme.ink }}
          />
        ) : (
          <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.08, letterSpacing: -0.8, color: theme.ink }}>
            {title || 'Titre / poste'}
          </div>
        )}
      </div>

      {/* ── Footer: contact email + availability ── */}
      <div style={{
        position: 'absolute', left: PAGE_PAD_X, right: PAGE_PAD_X, bottom: 52,
        borderTop: `1.5px solid ${theme.ink}`, paddingTop: 18,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: theme.faint, marginBottom: 4 }}>Contact</div>
          {editable ? (
            <input
              className="cover-edit-input"
              value={email}
              onChange={(e) => setEmail?.(e.target.value)}
              placeholder="email@exemple.com"
              style={{ ...inputReset, fontSize: 12.5, fontWeight: 600, color: theme.ink }}
            />
          ) : (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.ink }}>{email || '—'}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', paddingLeft: 16 }}>
          <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: theme.accent, marginRight: 8 }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: theme.ink }}>Disponible —&nbsp;</span>
          {editable ? (
            <input
              className="cover-edit-input"
              value={availability}
              onChange={(e) => setAvailability?.(e.target.value)}
              placeholder={dateStr}
              style={{ ...inputReset, width: 110, fontSize: 12.5, fontWeight: 600, color: theme.ink }}
            />
          ) : (
            <span style={{ fontSize: 12.5, fontWeight: 600, color: theme.ink }}>{dispo}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const logoBtn = {
  flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: 4, color: '#F0EEFF', fontSize: 9.5, fontWeight: 600, padding: '4px 6px',
  cursor: 'pointer', fontFamily: 'inherit',
};

export { computeTrigram };
export default CoverPage;
